import {AbsoluteFill, Img, interpolate, useCurrentFrame, Easing} from 'remotion';

/**
 * KenBurns3D — slow zoom + tilt + parallax on still photos
 *
 * Voidline signature treatment for archive photos / Briggs portraits / Wikimedia plates.
 * Replaces static stills (>8s static = auto-fail viral-judge rubric.B).
 *
 * Layers a depth illusion by splitting the image into N rendered copies
 * with different zoom/translate offsets and slight rotation, then animating
 * them with offset curves so foreground parallaxes faster than background.
 *
 * @example
 * <KenBurns3D
 *   asset="/assets/mary_celeste_deck.jpg"
 *   depthLayers={4}
 *   zoomFrom={1.0}
 *   zoomTo={1.12}
 *   tilt={2}
 *   tiltAxis="Y"
 *   parallaxStrength={0.6}
 *   ramp="ease-out-cubic"
 *   durationInFrames={240}
 * />
 */

type Props = {
  asset: string;
  depthLayers?: number;             // default 4
  zoomFrom?: number;                // default 1.0
  zoomTo?: number;                  // default 1.12
  tilt?: number;                    // degrees, default 2
  tiltAxis?: 'X' | 'Y';             // default 'Y'
  parallaxStrength?: number;        // 0-1, default 0.6
  ramp?: 'linear' | 'ease-out-cubic' | 'ease-in-out';
  durationInFrames: number;         // total animation length
  panX?: number;                    // optional horizontal pan, px @ 1.0 zoom
  panY?: number;
};

export const KenBurns3D: React.FC<Props> = ({
  asset,
  depthLayers = 4,
  zoomFrom = 1.0,
  zoomTo = 1.12,
  tilt = 2,
  tiltAxis = 'Y',
  parallaxStrength = 0.6,
  ramp = 'ease-out-cubic',
  durationInFrames,
  panX = 0,
  panY = 0,
}) => {
  const frame = useCurrentFrame();

  const easing =
    ramp === 'ease-out-cubic'
      ? Easing.bezier(0.33, 1, 0.68, 1)
      : ramp === 'ease-in-out'
      ? Easing.bezier(0.42, 0, 0.58, 1)
      : Easing.linear;

  const progress = interpolate(frame, [0, durationInFrames], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing,
  });

  const zoom = interpolate(progress, [0, 1], [zoomFrom, zoomTo]);
  const dx = panX * progress;
  const dy = panY * progress;
  const rot = tilt * progress;

  return (
    <AbsoluteFill style={{overflow: 'hidden', perspective: 1200}}>
      {Array.from({length: depthLayers}).map((_, i) => {
        const layerDepth = i / Math.max(1, depthLayers - 1); // 0 = back, 1 = front
        const layerZoom = zoom * (1 + layerDepth * parallaxStrength * 0.05);
        const layerDx = dx * (1 + layerDepth * parallaxStrength);
        const layerDy = dy * (1 + layerDepth * parallaxStrength);
        const layerOpacity = i === 0 ? 1 : 0.6;
        return (
          <Img
            key={i}
            src={asset}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: layerOpacity,
              transform: `
                translate3d(${layerDx}px, ${layerDy}px, ${i * 30}px)
                scale(${layerZoom})
                rotate${tiltAxis}(${rot}deg)
              `,
              transformStyle: 'preserve-3d',
              willChange: 'transform',
              filter: i === 0 ? 'none' : `blur(${i * 0.8}px) brightness(0.85)`,
            }}
          />
        );
      })}
    </AbsoluteFill>
  );
};
